import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface AdminUserRole {
  id: number;
  name: string;
}

export interface AdminUser {
  id: number;
  name: string;
  userName: string;
  email: string;
  roleId: number;
  isActive: boolean;
  role: AdminUserRole;
  roleName: string;
  phone: string;
}

export interface AdminsListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  admins: Array<AdminUser>;
  allAdmins: Array<AdminUser>;
  errors: unknown;
}

@Module
export default class AdminsModule extends VuexModule implements AdminsListInfo {
  admins = [] as Array<AdminUser>;
  allAdmins = [] as Array<AdminUser>;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get admins list
   * @returns Array<AdminUser>
   */
  get adminsList(): Array<AdminUser> {
    return this.admins;
  }

  get allAdminsList(): Array<AdminUser> {
    return this.allAdmins;
  }

  /**
   * Get total items
   * @returns number
   */
  get listCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_ADMIN_USERS](data) {
    this.admins = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_ADMINS](data) {
    this.allAdmins = data.data as Array<AdminUser>;
  }

  @Mutation
  [Mutations.SET_ADMIN_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_ADMINS_LIST]() {
    this.admins = [] as Array<AdminUser>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_ADMINS_STORE]() {
    this.context.commit(Mutations.RESET_ADMINS_LIST);
  }

  @Action
  [Actions.CREATE_ADMIN_ACCOUNT](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/admin', data)
      .then(() => {
        this.context.commit(Mutations.RESET_ADMINS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_ADMIN_ACCOUNT](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('admin', id)
      .then(() => {
        this.context.commit(Mutations.RESET_ADMINS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_ADMIN_ACCOUNT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('admin', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_ADMINS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_ADMIN_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_ADMIN_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_ADMIN_USER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('admin', id);
  }

  @Action
  [Actions.CHANGE_ADMIN_PASSWORD](credentials) {
    ApiService.setAuthorizationHeader();
    return ApiService.put(`/admin/change-password`, credentials)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.GET_ADMIN_USERS](input) {
    const params = {
      page: typeof input === 'string' ? 1 : this.page,
      name: input,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/admin', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ADMIN_USERS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.statusText],
        // });
      });
  }

  @Action
  [Actions.GET_ALL_ADMINS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/admin/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_ADMINS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.statusText],
        // });
      });
  }
}
